const groupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.CAMPAIGNS)

interface IFetchCampaignsQuery {
	page?: number
	perPage?: number
	workflowStatus?: ECampaignStatus | ECampaignStatus[]
	displayStatus?: ECampaignStatus | ECampaignStatus[]
	ownerId?: number | number[]
	department?: string | string[]
	distributionChannel?: string | string[]
	/** Comma-separated range: "YYYY-MM-DD,YYYY-MM-DD" */
	dateRange?: string
	requesterName?: string
	search?: string
	sortField?: 'name' | 'starts_at' | 'created_at' | 'id'
	sortOrder?: 'asc' | 'desc'
}

export const apiCampaigns = {
	fetchCampaigns: async (query: IFetchCampaignsQuery = {}): Promise<IApiResponseModel<ICampaignListItemModel[]>> => {
		const {
			page,
			perPage,
			sortField,
			sortOrder,
			workflowStatus,
			displayStatus,
			ownerId,
			department,
			distributionChannel,
			requesterName,
			dateRange,
			search,
		} = query
		const pagination = buildPagination(page, perPage)
		const sort = sortField
			? { [sortField]: sortOrder === 'desc' ? EApiSortType.DESC : EApiSortType.ASC }
			: undefined
		const { data, meta } = await apiCommon<ICampaign[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery(
				pagination,
				{
					workflow_status: workflowStatus,
					display_status: displayStatus,
					owner_id: ownerId,
					distribution_channel: distributionChannel,
					date_range: dateRange,
					requester_name: requesterName,
					department,
					search,
				},
				sort,
			),
		})
		return { data: data.map(CampaignListItem), meta }
	},

	fetchCampaignDetails: async (id: number): Promise<ICampaignModel> => {
		const { data } = await apiCommon<ICampaign>(`${groupPrefix}/${id}`, { method: 'GET' })
		return Campaign(data)
	},

	fetchCampaignAuditLog: async (
		id: number,
		{ page = 1, perPage = CAMPAIGN_DRAWER_HISTORY_PER_PAGE }: { page?: number; perPage?: number } = {},
	): Promise<IApiResponseModel<IAuditLogEntryModel[]>> => {
		// Backend zwraca stronicowaną listę zagnieżdżoną w polu data — apiCommon oddaje ją
		// jako IActivityLogResponseDto. Endpoint przyjmuje paginację jako page i per_page.
		const { data } = await apiCommon<IActivityLogResponseDto>(`${groupPrefix}/${id}/audit-log`, {
			method: 'GET',
			...createQuery(null, null, undefined, { page, per_page: perPage }),
		})
		return ActivityLogResponse(data)
	},

	createCampaign: async (payload: ICampaignCreatePayload): Promise<ICampaignModel> => {
		const { data } = await apiCommon<ICampaign>(`${groupPrefix}`, {
			method: 'POST',
			body: payload,
		})
		return Campaign(data)
	},

	updateCampaign: async (id: number, payload: ICampaignUpdatePayload): Promise<ICampaignModel> => {
		const { data } = await apiCommon<ICampaign>(`${groupPrefix}/${id}`, {
			method: 'PUT',
			body: payload,
		})
		return Campaign(data)
	},

	updateManyCampaigns: async (payload: ICampaignUpdateManyPayload): Promise<IBulkUpdateResult> => {
		const { data } = await apiCommon<IBulkUpdateResult>(`${groupPrefix}/update-many`, {
			method: 'PUT',
			body: payload,
		})
		return data
	},

	assignPromotions: async (id: number, payload: ICampaignAssignPromotionsPayload): Promise<void> => {
		await apiCommon(`${groupPrefix}/${id}/assign-promotions`, {
			method: 'POST',
			body: payload,
		})
	},

	detachPromotion: async (campaignId: number, promotionId: number): Promise<void> => {
		await apiCommon(`${groupPrefix}/${campaignId}/promotions/${promotionId}`, { method: 'DELETE' })
	},
}
